<template>
  <div class="projectile-viz-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>详见下方原理说明</div>
    </div>

    <div class="main-layout vertical">
      <!-- 渲染画板 -->
      <div class="canvas-3d-wrapper interactive">
        <svg width="100%" height="100%" viewBox="0 0 500 350" preserveAspectRatio="xMidYMid meet">
           <!-- 背景装饰 -->
           <rect width="500" height="350" fill="rgba(0,0,0,0.2)" />
           <line x1="50" y1="300" x2="450" y2="300" stroke="rgba(255,255,255,0.1)" />
           
           <!-- 轨迹 -->
           <path :d="fullPath" fill="none" stroke="rgba(255,255,255,0.05)" stroke-dasharray="4,4" />
           <path :d="activePath" fill="none" stroke="#2ed573" stroke-width="2" />

           <!-- 水平分运动 (匀速) -->
           <g v-if="showX" :transform="`translate(${50 + currentPos.x}, 310)`">
              <circle r="4" fill="#00d2ff" />
              <line x1="0" y1="0" x2="0" :y2="-(310 - (300 - currentPos.y))" stroke="#00d2ff" stroke-width="1" stroke-dasharray="2,2" />
              <text y="15" text-anchor="middle" fill="#00d2ff" font-size="8">v_x = const</text>
           </g>

           <!-- 竖直分运动 (自由落体) -->
           <g v-if="showY" :transform="`translate(30, ${300 - currentPos.y})`">
              <circle r="4" fill="#ff4757" />
              <line x1="0" y1="0" :x2="20 + currentPos.x" y2="0" stroke="#ff4757" stroke-width="1" stroke-dasharray="2,2" />
              <text x="-25" y="4" text-anchor="end" fill="#ff4757" font-size="8">v_y = gt</text>
           </g>

           <!-- 主体小球 -->
           <g :transform="`translate(${50 + currentPos.x}, ${300 - currentPos.y})`">
              <circle r="8" fill="#fff" />
              <circle r="4" fill="#2ed573" />
           </g>
        </svg>

        <!-- 实时数据悬浮窗 -->
        <div class="hud-mini">
           <div class="item"><span>T:</span> <span>{{ time.toFixed(2) }}s</span></div>
           <div class="item x"><span>X:</span> <span>{{ currentPos.x.toFixed(1) }}m</span></div>
           <div class="item y"><span>Y:</span> <span>{{ currentPos.y.toFixed(1) }}m</span></div>
        </div>
      </div>

      <!-- 控制区 -->
      <div class="bottom-controls">
         <div class="control-card glass small-p">
            <div class="row">
               <div class="col">
                  <label>发射初速度 (v₀)</label>
                  <el-slider v-model="v0" :min="20" :max="80" size="mini"></el-slider>
               </div>
               <div class="col">
                  <label>发射角度 (θ)</label>
                  <el-slider v-model="angle" :min="0" :max="90" size="mini"></el-slider>
               </div>
            </div>
            <div class="action-bar">
               <el-button type="primary" size="mini" @click="replay" icon="el-icon-video-play">重新点火发射</el-button>
               <span class="hint">观察：水平方向等间距位移，竖直方向位移越来越快。</span>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectileMotionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      v0: 50,
      angle: 45,
      time: 0,
      showX: true,
      showY: true,
      playing: true,
      g: 9.8
    }
  },
  computed: {
    currentPos() {
      const rad = this.angle * Math.PI / 180;
      const vx = this.v0 * Math.cos(rad);
      const vy = this.v0 * Math.sin(rad);
      const x = vx * this.time;
      const y = vy * this.time - 0.5 * this.g * this.time * this.time;
      return { x, y: Math.max(0, y) };
    },
    fullPath() {
      const rad = this.angle * Math.PI / 180;
      const vx = this.v0 * Math.cos(rad);
      const vy = this.v0 * Math.sin(rad);
      let d = "M 50 300";
      for(let t=0; t<10; t+=0.1) {
          const x = vx * t;
          const y = vy * t - 0.5 * this.g * t * t;
          if (y < 0) break;
          d += ` L ${50 + x} ${300 - y}`;
      }
      return d;
    },
    activePath() {
      const rad = this.angle * Math.PI / 180;
      const vx = this.v0 * Math.cos(rad);
      const vy = this.v0 * Math.sin(rad);
      let d = "M 50 300";
      for(let t=0; t<=this.time; t+=0.05) {
          const x = vx * t;
          const y = vy * t - 0.5 * this.g * t * t;
          if (y < 0) {
              d += ` L ${50 + x} 300`;
              break;
          }
          d += ` L ${50 + x} ${300 - y}`;
      }
      return d;
    }
  },
  methods: {
    replay() {
      this.time = 0;
      this.playing = true;
    },
    animate() {
      if (this.playing) {
        this.time += 0.05;
        if (this.currentPos.y <= 0 && this.time > 0.1) {
            this.playing = false;
        }
      }
      this.frame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.frame); }
}
</script>

<style scoped lang="scss">
.projectile-viz-pro {
  flex: 1; display: flex; flex-direction: column; gap: 15px; height: 100%; overflow: hidden;
}

.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .switches { display: flex; align-items: center; gap: 8px; .sw-label { font-size: 0.7rem; color: #888; } }
}

.canvas-3d-wrapper {
  flex: 1.2; background: rgba(0,0,0,0.3); border-radius: 12px; border: 1px solid rgba(255,255,255,0.05); position: relative;
  .hud-mini {
    position: absolute; top: 15px; right: 15px; background: rgba(0,0,0,0.6); padding: 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1);
    .item { font-size: 0.75rem; color: #fff; font-family: monospace; display: flex; justify-content: space-between; gap: 10px; margin-bottom: 2px; span:first-child { color: #888; } }
    .x span:last-child { color: #00d2ff; }
    .y span:last-child { color: #ff4757; }
  }
}

.bottom-controls {
  flex: 0.8; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; padding-bottom: 15px;
}

.control-card {
  background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 15px;
  &.small-p { padding: 12px 18px; }
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 12px; }
  .col { label { display: block; font-size: 0.7rem; color: #aaa; margin-bottom: 6px; } }
  .action-bar { display: flex; align-items: center; justify-content: space-between; .hint { font-size: 0.7rem; color: #555; font-style: italic; } }
}
</style>
